import notification from "antd/es/notification";
import { groupBy } from "lodash-es";
import { defineMessages } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import { Group } from "@mapmycustomers/shared/types/entity";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import { ListRequest } from "@mapmycustomers/shared/types/viewModel/internalModel/ListRequest";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import PlatformListRequest from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformListRequest";
import BulkApiError from "@mapmycustomers/shared/util/api/BulkApiError";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import groupFieldModel from "@app/util/fieldModel/GroupFieldModel";
import { getEntityTypeDisplayName } from "@app/util/ui";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  bulkDeleteGroups,
  bulkUpdateGroups,
  bulkUpdateGroupSharing,
  createGroup,
  deleteGroup,
  fetchAllGroups,
  fetchGroups,
  reloadGroups,
  updateEntities,
  updateGroup,
  updateGroupSharing,
} from "./actions";

const messages = defineMessages({
  bulkDeleteSuccess: {
    id: "groups.delete.bulk.success",
    defaultMessage: "{count, plural, one {Group} other {Groups}} successfully deleted",
    description: "Title of success notification when groups deleted in bulk successfully",
  },
  created: {
    id: "groups.create.success",
    defaultMessage: "{name} Group Created.",
    description: "Group created successfully",
  },
  deleted: {
    id: "groups.delete.success",
    defaultMessage: "Group successfully deleted",
    description: "Group deleted successfully",
  },
  entitiesUpdated: {
    id: "groups.updateEntities.success",
    defaultMessage: "{entityName} Edited.",
    description: "Entities are updated successfully from the Manage Group Modal",
  },
  updated: {
    id: "groups.update.success",
    defaultMessage: "{name} Group Edited.",
    description: "Group updated successfully",
  },
});

export function* onFetchGroups({ payload }: ReturnType<typeof fetchGroups.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const requestPayload: Omit<PlatformListRequest, "$columns"> = {
      $filters: payload.request?.filter
        ? convertToPlatformFilterModel(payload.request.filter, [], groupFieldModel)
        : ({} as PlatformFilterModel),
      $limit: payload.request?.range
        ? payload.request.range.endRow - payload.request.range.startRow
        : undefined,
      $offset: payload.request?.range ? payload.request.range.startRow : 0,
      $order: payload.request?.sort ? convertToPlatformSortModel(payload.request.sort) : undefined,
    };
    requestPayload.$filters!.includeAccessStatus = true;

    const response: ListResponse<Group> = yield callApi(
      "fetchGroups",
      organization.id,
      payload.entityType,
      requestPayload
    );

    yield put(fetchGroups.success({ entityType: payload.entityType, groups: response.data }));
  } catch (error) {
    yield put(fetchGroups.failure({ entityType: payload.entityType, error }));
  }
}

export function* onFetchAllGroups() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const requestPayload: Omit<PlatformListRequest, "$columns"> = {
      $filters: { includeAccessStatus: true },
      $limit: 10000,
      $order: "name",
    };

    const response: ListResponse<Group> = yield callApi(
      "fetchGroups",
      orgId,
      undefined,
      requestPayload
    );

    const groupsByType = groupBy(response.data, "entity") as Record<Group["entity"], Group[]>;

    yield put(fetchAllGroups.success(groupsByType));
  } catch {
    yield put(fetchAllGroups.failure());
    // error is handled in the onInitializeApp saga
  }
}

export function* onCreateGroup({
  payload: {
    activityTypesIds,
    cadenceInterval,
    color,
    entityType,
    name,
    onSuccess,
    userIdsToShareWith,
  },
}: ReturnType<typeof createGroup.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const response: Group = yield callApi(
      "createGroup",
      org.id,
      entityType,
      name,
      activityTypesIds,
      color,
      cadenceInterval
    );

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.created, {
          name,
        }),
      });
    }

    yield put(
      createGroup.success({
        entityType,
        group: response,
      })
    );

    if (userIdsToShareWith?.length) {
      const payload = userIdsToShareWith.map((user) => ({
        groupId: response.id,
        userId: user,
      }));
      yield callApi("bulkCreateGroupShare", org.id, payload);
    }

    onSuccess(response);

    // reload groups
    const request: Partial<ListRequest> = {
      range: { endRow: 999999, startRow: 0 }, // fetch all
      sort: [{ field: groupFieldModel.getByName("name")!, order: SortOrder.ASC }],
    };
    yield put(fetchGroups.request({ entityType, request }));
  } catch (error) {
    yield put(createGroup.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateGroup({
  payload: { entityType, group, noNotification, onSuccess },
}: ReturnType<typeof updateGroup.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedGroup: Group = yield callApi("updateGroup", orgId, entityType, group);

    // temporary fix since backend doesn't return items in PUT's response
    updatedGroup.items = group.items;

    const intl = i18nService.getIntl();
    if (intl && !noNotification) {
      notification.success({
        message: intl.formatMessage(messages.updated, {
          name: group.name,
        }),
      });
    }

    yield put(updateGroup.success({ entityType, group: updatedGroup }));
    onSuccess?.(entityType, updatedGroup);
  } catch (error) {
    yield put(updateGroup.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteGroup({
  payload: { entityType, groupId, onSuccess },
}: ReturnType<typeof deleteGroup.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteGroup", orgId, entityType, groupId);
    yield put(deleteGroup.success({ entityType, groupId }));
    if (onSuccess) {
      yield call(onSuccess, entityType, groupId);
    }
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.deleted),
      });
    }
  } catch (error) {
    yield put(deleteGroup.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateGroupSharing({
  payload: { entityType, group, onSuccess, userIdsToShareWith },
}: ReturnType<typeof updateGroupSharing.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const usersToAdd = userIdsToShareWith.filter((id) => !group.userIds?.includes(id));
    const usersToDelete = (group.userIds ?? []).filter((id) => !userIdsToShareWith.includes(id));
    const createPayload = (userIds: User["id"][]) =>
      userIds.map((userId) => ({ groupId: group.id, userId }));

    yield all([
      usersToAdd.length
        ? callApi("bulkCreateGroupShare", orgId, createPayload(usersToAdd))
        : undefined,
      usersToDelete.length
        ? callApi("bulkDeleteGroupShare", orgId, createPayload(usersToDelete))
        : undefined,
    ]);

    const updatedGroup: Group = yield callApi("fetchGroup", orgId, entityType, group.id);
    // There's a timeout after shared-groups api requests and the moment group is actually updated
    // so we're using a workaround for now. For more info: https://mapmycustomers.slack.com/archives/C03PGD7BLBY/p1663156032349549
    updatedGroup.userIds = userIdsToShareWith;

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.updated, {
          name: updatedGroup.name,
        }),
      });
    }

    yield put(updateGroupSharing.success({ entityType, group: updatedGroup }));
    onSuccess?.(entityType, updatedGroup);
  } catch (error) {
    yield put(updateGroupSharing.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateEntities({
  payload: { entities, entityType, groupIdsToAdd, groupIdsToDelete, onSuccess },
}: ReturnType<typeof updateEntities.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const entitiesIds = entities.map(({ id }) => id);
    yield all([
      all(
        (groupIdsToAdd ?? []).map((groupId) =>
          callApi("addToGroup", org.id, groupId, entityType, entitiesIds)
        )
      ),
      all(
        (groupIdsToDelete ?? []).map((groupId) =>
          callApi("deleteFromGroup", org.id, groupId, entityType, entitiesIds)
        )
      ),
    ]);
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.entitiesUpdated, {
          count: entities.length,
          entityName: getEntityTypeDisplayName(intl, entityType, {
            lowercase: false,
            plural: entities.length > 1,
          }),
        }),
      });
    }
    yield put(notifyAboutChanges({ entityType, updated: entities }));
    yield put(updateEntities.success());
    yield call(onSuccess);

    // reload groups to ensure their items count is updated
    const request: Partial<ListRequest> = {
      range: { endRow: 999999, startRow: 0 }, // fetch all
      sort: [{ field: groupFieldModel.getByName("name")!, order: SortOrder.ASC }],
    };
    yield put(fetchGroups.request({ entityType, request }));
  } catch (error) {
    yield put(updateEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onReloadGroups({ payload: { entityType } }: ReturnType<typeof reloadGroups>) {
  const request: Partial<ListRequest> = {
    range: { endRow: 999999, startRow: 0 }, // fetch all
    sort: [{ field: groupFieldModel.getByName("name")!, order: SortOrder.ASC }],
  };
  yield put(fetchGroups.request({ entityType, request }));
}

export function* onBulkDeleteGroups({
  payload: { entityType, failureCallback, groups },
}: ReturnType<typeof bulkDeleteGroups.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedGroupIds: Group["id"][] = yield callApi(
      "bulkDeleteGroups",
      orgId,
      entityType,
      groups.map(({ id }) => id)
    );

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        message: intl.formatMessage(messages.bulkDeleteSuccess, { count: updatedGroupIds.length }),
      });
    }
    yield put(bulkDeleteGroups.success({ entityType, groupIds: updatedGroupIds }));
  } catch (error) {
    const validationErrors = getValidationErrors(error) as unknown as BulkApiError[];

    if (validationErrors.length) {
      yield call(failureCallback, validationErrors);
    } else {
      yield put(handleError({ error }));
    }
    yield put(bulkDeleteGroups.failure(error));
  }
}

export function* onBulkUpdateGroups({
  payload: { entityType, groups },
}: ReturnType<typeof bulkUpdateGroups.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("bulkUpdateGroups", orgId, entityType, groups);

    yield put(reloadGroups({ entityType }));
    yield put(bulkUpdateGroups.success());
  } catch (error) {
    yield put(bulkUpdateGroups.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onBulkGroupsSharing({
  payload: { entityType, groups, userIds },
}: ReturnType<typeof bulkUpdateGroupSharing.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const usersToAdd = groups.flatMap(({ id: groupId }) =>
      userIds.map((userId) => ({ groupId, userId }))
    );

    const usersToDelete = groups.flatMap(({ id: groupId, userIds: groupUserIds }) =>
      (groupUserIds ?? [])
        .filter((id) => !userIds.includes(id))
        .map((userId) => ({ groupId, userId }))
    );

    yield all([
      usersToAdd.length ? callApi("bulkCreateGroupShare", orgId, usersToAdd) : undefined,
      usersToDelete.length ? callApi("bulkDeleteGroupShare", orgId, usersToDelete) : undefined,
    ]);

    const groupIds = groups.map(({ id }) => id);

    yield put(bulkUpdateGroupSharing.success({ entityType, groupIds, userIds }));
  } catch (error) {
    yield put(bulkUpdateGroupSharing.failure(error));
    yield put(handleError({ error }));
  }
}

export function* groupsSaga() {
  yield takeEvery(fetchGroups.request, onFetchGroups); // must be takeEvery since actions might have different entityType in them
  yield takeLatest(fetchAllGroups.request, onFetchAllGroups);
  yield takeEvery(reloadGroups, onReloadGroups); // must be takeEvery since actions might have different entityType in them
  yield takeEvery(createGroup.request, onCreateGroup);
  yield takeEvery(updateGroup.request, onUpdateGroup);
  yield takeEvery(deleteGroup.request, onDeleteGroup);
  yield takeEvery(updateGroupSharing.request, onUpdateGroupSharing);
  yield takeEvery(updateEntities.request, onUpdateEntities);
  yield takeLatest(bulkUpdateGroups.request, onBulkUpdateGroups);
  yield takeLatest(bulkDeleteGroups.request, onBulkDeleteGroups);
  yield takeLatest(bulkUpdateGroupSharing.request, onBulkGroupsSharing);
}
